<template>
  <div class="arp">
    <div class="arp-section">
      <div class="arp-section__title">{{ t('dimSection') }}</div>
      <ul class="arp-list">
        <li
          v-for="item in dimensions"
          :key="item.key"
          class="arp-item"
          :class="itemClass('dim', item)"
          :draggable="item.selected"
          @click="onClick('dim', item)"
          @dragstart="onDragStart('dim', item, $event)"
          @dragover="onDragOver('dim', item, $event)"
          @dragleave="onDragLeave(item)"
          @drop="onDrop('dim', item, $event)"
          @dragend="onDragEnd"
        >
          <!-- 勾选框不接事件，点击一律由整行处理，否则点在框上会触发两次 -->
          <el-checkbox class="arp-item__box" :value="item.selected" />
          <span class="arp-item__name" :title="item.name">{{ item.name }}</span>
          <i v-if="item.selected" class="el-icon-rank arp-item__handle"></i>
        </li>
      </ul>
    </div>

    <div class="arp-section">
      <div class="arp-section__title">{{ t('metricSection') }}</div>
      <ul class="arp-list">
        <li
          v-for="item in metrics"
          :key="item.key"
          class="arp-item"
          :class="itemClass('metric', item)"
          :draggable="item.selected"
          @click="onClick('metric', item)"
          @dragstart="onDragStart('metric', item, $event)"
          @dragover="onDragOver('metric', item, $event)"
          @dragleave="onDragLeave(item)"
          @drop="onDrop('metric', item, $event)"
          @dragend="onDragEnd"
        >
          <el-checkbox class="arp-item__box" :value="item.selected" />
          <span class="arp-item__name" :title="item.name">{{ item.name }}</span>
          <i v-if="item.selected" class="el-icon-rank arp-item__handle"></i>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AbroadReportPanel',
  props: {
    // [{ key, name, selected }]，顺序由页面排好：已选在前，未选在后
    dimensions: {
      type: Array,
      default: () => []
    },
    metrics: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // 正在拖的那一项 { type, key }，和 dragover 时经过的那一项 key
      dragging: null,
      overKey: ''
    }
  },
  computed: {
    // 指标至少保留 1 项（PRD 2.6.1.6），只剩一个时它自己不能被取消
    metricLocked() {
      const on = this.metrics.filter(item => item.selected)
      return on.length <= 1 ? (on[0] || {}).key : ''
    }
  },
  methods: {
    t(key) {
      return this.$t('abroadStat.' + key)
    },
    isLocked(type, item) {
      return type === 'metric' && item.selected && item.key === this.metricLocked
    },
    itemClass(type, item) {
      const side = this.dropSide(type, item)
      return {
        'is-on': item.selected,
        'is-locked': this.isLocked(type, item),
        // 插入线画在哪一侧要和真实落点一致：往下拖是插到目标之后，往上拖是插到目标之前
        'is-drop-before': side === 'before',
        'is-drop-after': side === 'after',
        'is-dragging': this.dragging !== null && this.dragging.key === item.key
      }
    },
    // 拖到 item 上时插入线的位置。'' 表示不画
    dropSide(type, item) {
      if (this.overKey !== item.key || !this.canDrop(type, item)) return ''
      const list = type === 'dim' ? this.dimensions : this.metrics
      const keys = list.map(one => one.key)
      return keys.indexOf(this.dragging.key) < keys.indexOf(item.key) ? 'after' : 'before'
    },
    onClick(type, item) {
      if (this.isLocked(type, item)) return
      this.$emit('toggle', type, item.key)
    },
    onDragStart(type, item, e) {
      if (!item.selected) return
      this.dragging = { type: type, key: item.key }
      // 不设 setData 的话 firefox 不认这次拖拽
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move'
        try {
          e.dataTransfer.setData('text/plain', item.key)
        } catch (err) {
          // IE 下 setData 只认 'text'，拖不了就算了，勾选还能用
        }
      }
    },
    // 只允许在同一段的已选区里换位置
    canDrop(type, item) {
      return this.dragging !== null &&
        this.dragging.type === type &&
        this.dragging.key !== item.key &&
        item.selected
    },
    onDragOver(type, item, e) {
      if (!this.canDrop(type, item)) return
      // 不 preventDefault 就不会触发 drop
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
      this.overKey = item.key
    },
    onDragLeave(item) {
      if (this.overKey === item.key) this.overKey = ''
    },
    onDrop(type, item, e) {
      if (!this.canDrop(type, item)) return
      e.preventDefault()
      this.$emit('reorder', type, this.dragging.key, item.key)
      this.onDragEnd()
    },
    onDragEnd() {
      this.dragging = null
      this.overKey = ''
    }
  }
}
</script>

<style lang="scss" scoped>
$main: #3361ff;
$text: #1f2329;
$text-sub: #4e5969;
$line: #f0f1f3;

.arp {
  padding: 6px 0;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid $line;
  border-radius: 8px;
}
.arp-section + .arp-section {
  margin-top: 2px;
  border-top: 1px solid $line;
  padding-top: 6px;
}
.arp-section__title {
  padding: 4px 14px 4px;
  font-size: 12px;
  color: #8f959e;
}
.arp-list {
  margin: 0;
  padding: 0;
  list-style: none;
}
.arp-item {
  position: relative;
  display: flex;
  align-items: center;
  height: 30px;
  padding: 0 10px 0 14px;
  font-size: 13px;
  color: $text-sub;
  cursor: pointer;

  &:hover {
    background: #f5f6f7;
  }
  /* 手柄平时不显示，鼠标移到那一行才出来，不然十几行图标很吵 */
  &:hover .arp-item__handle {
    visibility: visible;
  }
  &.is-on {
    color: $text;
  }
  /* 只剩一个指标时不许再取消 */
  &.is-locked {
    color: #c0c4cc;
    cursor: not-allowed;

    &:hover {
      background: transparent;
    }
  }
  &.is-dragging {
    opacity: 0.4;
  }
  /* 插入位置的横线 */
  &.is-drop-before::before,
  &.is-drop-after::before {
    content: '';
    position: absolute;
    left: 10px;
    right: 10px;
    height: 2px;
    background: $main;
  }
  &.is-drop-before::before {
    top: -1px;
  }
  &.is-drop-after::before {
    bottom: -1px;
  }
}
.arp-item__box {
  flex: 0 0 auto;
  /* 事件全交给整行，勾选框只负责画 */
  pointer-events: none;
}
.arp-item__name {
  flex: 1;
  min-width: 0;
  margin-left: 6px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.arp-item__handle {
  flex: 0 0 auto;
  visibility: hidden;
  margin-left: 8px;
  font-size: 13px;
  color: #c0c4cc;
  cursor: move;
}
</style>
